<template>
  <v-tabs v-model="tabsNavigation" show-arrows>
    <v-tab v-if="hasExerciseTab">
      Exercise
    </v-tab>
    <v-tab v-if="hasVideosTab">
      Videos
    </v-tab>
    <v-tab v-if="hasInstructionTab">
      Instruction
    </v-tab>
    <v-tab v-if="hasTipsTab">
      Tips
    </v-tab>
    <v-tab v-if="hasDetailsTab">
      Detailed instructions
    </v-tab>
    <v-tab v-if="hasQuestionnaireTab">
      Questionnaire
    </v-tab>

    <v-tabs-items v-model="tabsNavigation" class="pa-5">
      <v-tab-item v-if="hasExerciseTab">
        <exercise-details-tab-content :exercise="item" />
      </v-tab-item>

      <v-tab-item v-if="hasVideosTab">
        <dl>
          <dt>Video url (male)</dt>
          <dd>{{ item.video && item.video.url }}</dd>
          <div v-if="item.video && item.video.url && item.video.url.length">
            <dt>Preview</dt>
            <dd>
              <video :src="item.video.url" height="240px" preload="metadata" controls />
            </dd>
          </div>
          <dt>Video url (female)</dt>
          <dd>{{ item.videoFemale && item.videoFemale.url }}</dd>
          <div v-if="item.videoFemale && item.videoFemale.url && item.videoFemale.url.length">
            <dt>Preview</dt>
            <dd>
              <video :src="item.videoFemale.url" height="240px" preload="metadata" controls />
            </dd>
          </div>
        </dl>
      </v-tab-item>

      <v-tab-item v-if="hasInstructionTab">
        <dl>
          <dt>Image url (male)</dt>
          <dd>{{ item.instruction && item.instruction.imageUrl }}</dd>
          <div v-if="item.instruction && item.instruction.imageUrl && item.instruction.imageUrl.length">
            <dt>Preview</dt>
            <dd>
              <v-img-ex :src="item.instruction.imageUrl" max-width="400" max-height="400" v-on="$listeners" />
            </dd>
          </div>
          <dt>Image url (female)</dt>
          <dd>{{ item.instruction && item.instruction.imageUrlFemale }}</dd>
          <div v-if="item.instruction && item.instruction.imageUrlFemale && item.instruction.imageUrlFemale.length">
            <dt>Preview</dt>
            <dd>
              <v-img-ex :src="item.instruction.imageUrlFemale" max-width="400" max-height="400" v-on="$listeners" />
            </dd>
          </div>
          <dt>Description</dt>
          <dd class="text-pre-line">
            {{ item.instruction && item.instruction.description }}
          </dd>
        </dl>
      </v-tab-item>

      <v-tab-item v-if="hasTipsTab">
        <div v-for="(tip, tipIndex) in item.tips || []" :key="`tip-${tipIndex}`" class="mb-10">
          <h3>Tip {{ tipIndex + 1 }}/{{ (item.tips && item.tips.length) || 0 }}</h3>
          <v-divider class="mb-5"></v-divider>
          <tip-details-tab-content :tip="tip" />
        </div>
      </v-tab-item>

      <v-tab-item v-if="hasDetailsTab">
        <dl class="mb-5">
          <dt>Title</dt>
          <dd v-html="sanitizeRichText((item.details && item.details.instructions && item.details.instructions.title) || 'No Data')"></dd>
        </dl>
        <div
          v-for="(image, imageIndex) in (item.details &&
            item.details.instructions &&
            item.details.instructions.images) ||
            []"
          :key="`image-${imageIndex}`"
          class="mb-5"
        >
          <h3>
            Image {{ imageIndex + 1 }}/{{
              (item.details &&
                item.details.instructions &&
                item.details.instructions.images &&
                item.details.instructions.images.length) ||
                0
            }}
          </h3>
          <v-divider class="mb-5"></v-divider>
          <dl>
            <dt>Title</dt>
            <dd>{{ image.title }}</dd>
            <dt>Image url</dt>
            <dd>{{ image.imageUrl }}</dd>
            <div v-if="image.imageUrl && image.imageUrl.length">
              <dt>Preview</dt>
              <dd>
                <v-img-ex :src="image.imageUrl" max-width="400" max-height="400" v-on="$listeners" />
              </dd>
            </div>
          </dl>
        </div>
      </v-tab-item>

      <v-tab-item v-if="hasQuestionnaireTab">
        <dl class="mb-5">
          <dt>Title</dt>
          <dd>{{ item.questionnaire.title }}</dd>
          <dt>When to fill</dt>
          <dd>{{ item.questionnaire.whenToFill }}</dd>
        </dl>
        <div
          v-for="(section, sectionIndex) in item.questionnaire.sections"
          :key="`questionnaire-section-${sectionIndex}`"
          class="mb-5"
        >
          <h3>
            Question {{ sectionIndex + 1 }}/{{ item.questionnaire.sections.length }}
          </h3>
          <v-divider class="mb-5"></v-divider>
          <dl>
            <dt>Question text</dt>
            <dd>{{ section.questionText }}</dd>
            <dt>Question phrasing</dt>
            <dd>{{ section.phrasing }}</dd>
            <dt>Answers</dt>
            <dd>
              <ol>
                <li
                  v-for="(option, optionIndex) in ((section.choiceList && section.choiceList[0] && section.choiceList[0].options) || [])"
                  :key="`questionnaire-section-${sectionIndex}-option-${optionIndex}`"
                >
                  {{ option }}
                </li>
              </ol>
            </dd>
          </dl>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </v-tabs>
</template>

<script>
import { ref, computed } from 'vue'
import ExerciseDetailsTabContent from '@/components/ExerciseDetailsTabContent.vue'
import TipDetailsTabContent from '@/components/TipDetailsTabContent.vue'
import VImgEx from '@/components/VImgEx.vue'
import { sanitizeRichText } from '@/utils/richText'

export default {
  components: {
    ExerciseDetailsTabContent,
    TipDetailsTabContent,
    VImgEx,
  },

  props: {
    item: {
      required: true,
      type: Object,
    },
  },

  setup(props) {
    const tabsNavigation = ref(0)

    const hasExerciseTab = computed(() => !!props.item)
    const hasVideosTab = computed(
      () => (props.item.video && props.item.video.url) || (props.item.videoFemale && props.item.videoFemale.url),
    )
    const hasInstructionTab = computed(
      () => props.item.instruction
        && (props.item.instruction.imageUrl
          || props.item.instruction.imageUrlFemale
          || props.item.instruction.description),
    )
    const hasTipsTab = computed(() => Array.isArray(props.item.tips) && props.item.tips.length > 0)
    const hasDetailsTab = computed(
      () => props.item.details
        && props.item.details.instructions
        && Array.isArray(props.item.details.instructions.images)
        && props.item.details.instructions.images.length > 0,
    )
    const hasQuestionnaireTab = computed(
      () => props.item.questionnaire
        && props.item.questionnaire.title
        && Array.isArray(props.item.questionnaire.sections)
        && props.item.questionnaire.sections.length > 0,
    )

    return {
      tabsNavigation,
      hasExerciseTab,
      hasVideosTab,
      hasInstructionTab,
      hasTipsTab,
      hasDetailsTab,
      hasQuestionnaireTab,
      sanitizeRichText,
    }
  },
}
</script>
